import { Box, Text, Image, Button, Code, Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody, useDisclosure } from '@chakra-ui/react';
import { ContentBlock as ContentBlockType } from '../data/projectsData';
import { Quote } from 'lucide-react';
import theme from '../themes/theme';

interface ContentBlockProps {
    block: ContentBlockType;
}

export default function ContentBlock({ block }: ContentBlockProps) {
    const { isOpen, onOpen, onClose } = useDisclosure();

    switch (block.type) {
        case 'image':
            const getImageMaxWidth = () => {
                switch (block.size) {
                    case 'small': return '300px';
                    case 'medium': return '500px';
                    case 'large': return '700px';
                    case 'full': return '100%';
                    default: return '800px'; // Default if no size specified
                }
            };

            return (
                <>
                    <Box mb={6} display="flex" justifyContent="center">
                        <Image
                            src={block.src}
                            alt={block.alt || ''}
                            borderRadius="lg"
                            width="100%"
                            maxW={getImageMaxWidth()}
                            objectFit="contain"
                            transition="opacity 0.2s ease"
                            _hover={{ opacity: 0.85 }}
                            onClick={onOpen}
                        />
                    </Box>

                    <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl">
                        <ModalOverlay bg="blackAlpha.800"/>
                        <ModalContent
                            bg="transparent"
                            boxShadow="none"
                            maxW="90vw"
                            maxH="90vh"
                        >
                            <ModalCloseButton
                                color="white"
                                bg="blackAlpha.600"
                                borderRadius="full"
                                _hover={{ bg: 'blackAlpha.800' }}
                                cursor={'none'}
                            />
                            <ModalBody
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                p={0}
                            >
                                <Image
                                    src={block.src}
                                    alt={block.alt || ''}
                                    borderRadius="lg"
                                    maxW="100%"
                                    maxH="90vh"
                                    objectFit="contain"
                                    onClick={onClose}
                                />
                            </ModalBody>
                        </ModalContent>
                    </Modal>
                </>
            );

        case 'caption':
            return (
                <Text
                    fontSize="sm"
                    color="gray.400"
                    textAlign="center"
                    fontStyle="italic"
                    mb={8}
                >
                    {block.text}
                </Text>
            );

        case 'text':
            return (
                <Text
                    fontSize="lg"
                    color="white.200"
                    lineHeight="1.8"
                    mb={6}
                >
                    {block.content}
                </Text>
            );

        case 'header':
            return (
                <Text
                    fontSize="4xl"
                    fontFamily="Source Code Pro Variable"
                    fontWeight="600"
                    color="white.200"
                    mb={4}
                    mt={8}
                >
                    {block.text}
                </Text>
            );

        case 'subheader':
            return (
                <Text
                    fontSize="2xl"
                    fontFamily="Source Code Pro Variable"
                    fontWeight="500"
                    color="white.200"
                    mb={3}
                    mt={6}
                >
                    {block.text}
                </Text>
            );

        case 'primary-button':
            return (
                <Button
                    as="a"
                    href={block.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    fontFamily={"Source Code Pro Variable"}
                    fontWeight={"600"}
                    fontSize={{ base: "lg", md: "2xl" }}
                    color={"black.200"}
                    backgroundColor={"blue.300"}
                    borderRadius={0}
                    width={"100%"}
                    maxW="100%"
                    height={{ base: "60px", md: "80px" }}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    mb={3}
                    mt={6}
                >
                    {block.text}
                </Button>
            );

        case 'secondary-button':
            return (
                <Box mb={6} mt={4}>
                    <Button
                        as="a"
                        href={block.link}
                        target={block.link.startsWith('http') ? '_blank' : undefined}
                        rel={block.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        colorScheme="green"
                        variant="outline"
                        size="lg"
                        fontFamily="Source Code Pro Variable"
                    >
                        {block.text}
                    </Button>
                </Box>
            );

        case 'code':
            return (
                <Box mb={8} mt={4}>
                    <Code
                        display="block"
                        whiteSpace="pre"
                        p={4}
                        borderRadius="md"
                        bg="gray.800"
                        color="green.300"
                        fontSize="sm"
                        fontFamily="monospace"
                        overflowX="auto"
                    >
                        {block.code}
                    </Code>
                </Box>
            );

            case 'testimonial':
                return (
                    <Box
                        position="relative"
                        my={12}
                        mx="auto"
                        w={"100%"}
                    >
                        {/* Main testimonial card */}
                        <Box
                            position="relative"
                            bg="#111827"
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="rgba(94, 234, 212, 0.15)"
                            p={{ base: 6, md: 8 }}
                            transition="all 0.3s ease-in-out"
                            _hover={{
                                borderColor: "rgba(94, 234, 212, 0.35)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 8px 24px rgba(94, 234, 212, 0.08)"
                            }}
                        >
                            {/* Quote icon */}
                            <Box
                                position="absolute"
                                top={{ base: 4, md: 6 }}
                                left={{ base: 4, md: 6 }}
                                color={theme.colors.blue[300]}
                                opacity={0.2}
                            >
                                <Quote size={28} />
                            </Box>

                            {/* Quote text */}
                            <Text
                                fontSize={{ base: "lg", md: "xl" }}
                                color={theme.colors.white[200]}
                                lineHeight="1.8"
                                mb={6}
                                mt={4}
                                fontStyle="italic"
                                position="relative"
                            >
                                "{block.text}"
                            </Text>

                            {/* Divider line */}
                            <Box
                                height="1px"
                                width="50px"
                                bg={theme.colors.blue[300]}
                                opacity={0.4}
                                mb={4}
                            />

                            {/* Attribution */}
                            <Box>
                                <Text
                                    fontSize={{ base: "md", md: "lg" }}
                                    fontFamily="Source Code Pro Variable"
                                    fontWeight="600"
                                    color={theme.colors.white[200]}
                                    mb={1}
                                >
                                    {block.name}
                                </Text>
                                <Text
                                    fontSize={{ base: "sm", md: "md" }}
                                    color={theme.colors.white[300]}
                                    fontStyle={"italic"}
                                    opacity={0.7}
                                >
                                    {block.position}
                                </Text>
                            </Box>

                        </Box>
                    </Box>
                );

        default:
            return null;
    }
}